// Animated water surface: a handful of hand-tuned sine waves summed together
// (a simplified take on Gerstner/FFT ocean waves - see three.js's webgl_shaders_ocean
// example for the classic reference), displacing the plane vertically and perturbing
// the normal via the analytic derivative of the same sum so lighting reacts to the waves.
// Also reflects the scene's skybox cubemap at grazing angles and adds a simple
// wave-crest foam term, rather than the flat two-colour fresnel mix of v1.
const vertexShader = /* glsl */ `
    attribute vec3 vertex_position;
    attribute vec3 vertex_normal;
    attribute vec2 vertex_texCoord0;

    uniform mat4 matrix_model;
    uniform mat4 matrix_viewProjection;
    uniform mat3 matrix_normal;

    uniform float uTime;
    uniform float uAmplitude;

    varying vec3 vWorldPos;
    varying vec3 vNormal;
    varying vec2 vUv;
    varying float vHeight;

    // six wave components: (dirX, dirZ, wavelength, speed)
    const vec4 wave0 = vec4(1.0, 0.2, 3.1, 1.0);
    const vec4 wave1 = vec4(-0.6, 0.8, 1.7, 1.3);
    const vec4 wave2 = vec4(0.3, -0.9, 0.9, 1.8);
    const vec4 wave3 = vec4(-0.8, -0.4, 0.45, 2.4);
    const vec4 wave4 = vec4(0.9, 0.6, 0.28, 3.1);
    const vec4 wave5 = vec4(-0.3, 0.95, 0.6, 2.0);

    float waveHeight(vec4 w, vec2 p, float t) {
        vec2 d = normalize(w.xy);
        float k = 6.28318 / max(w.z, 0.001);
        float phase = k * dot(d, p) + t * w.w;
        return sin(phase);
    }

    vec2 waveSlope(vec4 w, vec2 p, float t) {
        vec2 d = normalize(w.xy);
        float k = 6.28318 / max(w.z, 0.001);
        float phase = k * dot(d, p) + t * w.w;
        float c = cos(phase) * k;
        return d * c;
    }

    void main() {
        vec2 p = vertex_position.xz;

        float h = waveHeight(wave0, p, uTime) * 0.42
                + waveHeight(wave1, p, uTime) * 0.26
                + waveHeight(wave2, p, uTime) * 0.14
                + waveHeight(wave3, p, uTime) * 0.08
                + waveHeight(wave4, p, uTime) * 0.06
                + waveHeight(wave5, p, uTime) * 0.04;

        vec2 slope = waveSlope(wave0, p, uTime) * 0.42
                   + waveSlope(wave1, p, uTime) * 0.26
                   + waveSlope(wave2, p, uTime) * 0.14
                   + waveSlope(wave3, p, uTime) * 0.08
                   + waveSlope(wave4, p, uTime) * 0.06
                   + waveSlope(wave5, p, uTime) * 0.04;

        vec3 displaced = vertex_position + vec3(0.0, h * uAmplitude, 0.0);
        vec3 localNormal = normalize(vec3(-slope.x * uAmplitude, 1.0, -slope.y * uAmplitude));

        vec4 worldPos = matrix_model * vec4(displaced, 1.0);
        vWorldPos = worldPos.xyz;
        vNormal = normalize(matrix_normal * localNormal);
        vUv = vertex_texCoord0;
        vHeight = h;

        gl_Position = matrix_viewProjection * worldPos;
    }
`;

const fragmentShader = /* glsl */ `
    precision highp float;

    uniform vec3 view_position;
    uniform vec3 uShallowColor;
    uniform vec3 uDeepColor;
    uniform float uTime;
    uniform float uFoamIntensity;
    uniform samplerCube uEnvironmentMap;
    uniform float uHasEnvironmentMap;

    varying vec3 vWorldPos;
    varying vec3 vNormal;
    varying vec2 vUv;
    varying float vHeight;

    // fixed sun direction matching this app's default key light (scene.ts euler 55, 30, 0)
    const vec3 sunDir = normalize(vec3(0.4056, -0.8192, 0.4056));

    // cheap hash-based pseudo-noise, used only to break up the foam pattern
    float hash(vec2 p) {
        return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
    }

    void main() {
        vec3 n = normalize(vNormal);
        vec3 viewDir = normalize(view_position - vWorldPos);

        // steeper falloff and a lower cap so reflection only shows at truly
        // grazing angles instead of washing out most of the surface
        float fresnel = pow(1.0 - clamp(dot(n, viewDir), 0.0, 1.0), 5.0) * 0.6;

        // reflect the sky at grazing angles when a skybox is available, otherwise
        // fall back to the flat deep-water colour. blur the sample across a small
        // jittered cluster so it reads as rough water rather than a mirror.
        vec3 reflectDir = reflect(-viewDir, n);
        vec3 tangent = normalize(cross(n, vec3(0.0, 0.0, 1.0) + vec3(0.0001)));
        vec3 bitangent = cross(n, tangent);
        vec3 skyColor = textureCube(uEnvironmentMap, reflectDir).rgb;
        skyColor += textureCube(uEnvironmentMap, normalize(reflectDir + tangent * 0.12)).rgb;
        skyColor += textureCube(uEnvironmentMap, normalize(reflectDir - tangent * 0.12)).rgb;
        skyColor += textureCube(uEnvironmentMap, normalize(reflectDir + bitangent * 0.12)).rgb;
        skyColor += textureCube(uEnvironmentMap, normalize(reflectDir - bitangent * 0.12)).rgb;
        skyColor *= 0.2;
        vec3 reflectionColor = mix(uDeepColor, mix(uDeepColor, skyColor, 0.55), uHasEnvironmentMap);

        vec3 baseColor = mix(uShallowColor, reflectionColor, fresnel);

        vec3 halfDir = normalize(viewDir - sunDir);
        float spec = pow(clamp(dot(n, halfDir), 0.0, 1.0), 120.0);

        // wave-crest foam: whiten where displaced height exceeds a threshold,
        // broken up by a fast-scrolling noise pattern so it doesn't look uniform
        float crest = smoothstep(0.55, 0.85, vHeight);
        float noise = hash(floor(vWorldPos.xz * 8.0 + uTime * 1.5));
        float foam = crest * mix(0.4, 1.0, noise) * uFoamIntensity;

        vec3 color = baseColor + vec3(1.0, 0.98, 0.9) * spec;
        color = mix(color, vec3(1.0), clamp(foam, 0.0, 1.0));

        gl_FragColor = vec4(color, 0.85);
    }
`;

export { vertexShader, fragmentShader };
